import React, { useState, useEffect, useCallback } from 'react';
import { apiService } from '../../../services/api';
import './QuickAddNodeModal.css';

/**
 * QuickAddNodeModal - Simplified node creation with auto-populated Platform fields.
 *
 * Only requires: name, description, kind.
 * Optional: sourceText.
 * All Platform parameters (NodeRateCoding, BoundedData, NlRawText, Pinecone) are
 * auto-populated by the backend.
 */

const DEFAULT_KINDS = [
  'Concept', 'Fact', 'Process', 'Entity', 'Attribute',
  'Relationship', 'Event', 'Rule', 'Condition',
];

function QuickAddNodeModal({ isOpen, onClose, onNodeCreated, graphId }) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [kind, setKind] = useState('Concept');
  const [sourceText, setSourceText] = useState('');
  const [showSourceText, setShowSourceText] = useState(false);
  const [aliases, setAliases] = useState([]);
  const [aliasInput, setAliasInput] = useState('');
  const [showAliases, setShowAliases] = useState(false);

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Fetch available node kinds from the database
  const [kinds, setKinds] = useState(DEFAULT_KINDS);
  useEffect(() => {
    if (!isOpen) return;
    (async () => {
      try {
        const resp = await apiService.listNodeKinds();
        if (resp.success && resp.kinds && resp.kinds.length > 0) {
          const names = resp.kinds.map((k) => k.kindName || k.label).filter(Boolean);
          if (names.length > 0) setKinds(names);
        }
      } catch {
        // Fall back to defaults
      }
    })();
  }, [isOpen]);

  // Reset form on open
  useEffect(() => {
    if (isOpen) {
      setName('');
      setDescription('');
      setKind('Concept');
      setSourceText('');
      setShowSourceText(false);
      setAliases([]);
      setAliasInput('');
      setShowAliases(false);
      setError(null);
      setSuccess(null);
    }
  }, [isOpen]);

  const handleSubmit = useCallback(async () => {
    if (!name.trim()) { setError('Name is required'); return; }
    if (!description.trim()) { setError('Description is required'); return; }
    if (!kind) { setError('Kind is required'); return; }

    setSaving(true);
    setError(null);
    setSuccess(null);

    try {
      const result = await apiService.quickAddNode({
        graphId,
        name: name.trim(),
        description: description.trim(),
        kind,
        sourceText: sourceText.trim() || undefined,
        aliases: aliases.length > 0 ? aliases : undefined,
      });

      if (result.success) {
        setSuccess('Node created successfully');
        if (onNodeCreated) onNodeCreated(result.node || result);
        setTimeout(() => onClose(), 600);
      } else {
        setError(result.message || 'Failed to create node');
      }
    } catch (err) {
      setError(err?.response?.data?.message || err.message || 'Failed to create node');
    } finally {
      setSaving(false);
    }
  }, [graphId, name, description, kind, sourceText, onNodeCreated, onClose]);

  if (!isOpen) return null;

  return (
    <div className="qa-modal-overlay" onClick={onClose}>
      <div className="qa-modal" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="qa-modal-header">
          <div>
            <p className="qa-modal-eyebrow">Quick Add</p>
            <h2>New Node</h2>
          </div>
          <button className="qa-modal-close" onClick={onClose}>&times;</button>
        </div>

        {/* Body */}
        <div className="qa-modal-body">
          {error && <div className="qa-modal-alert qa-modal-alert--error">{error}</div>}
          {success && <div className="qa-modal-alert qa-modal-alert--success">{success}</div>}

          <div className="qa-modal-field">
            <label>Name <span className="qa-required">*</span></label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Photosynthesis"
              autoFocus
            />
          </div>

          <div className="qa-modal-field">
            <label>Description <span className="qa-required">*</span></label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Describe what this node represents..."
              rows={3}
            />
          </div>

          <div className="qa-modal-field">
            <label>Kind <span className="qa-required">*</span></label>
            <select value={kind} onChange={(e) => setKind(e.target.value)}>
              {kinds.map((k) => (
                <option key={k} value={k}>{k}</option>
              ))}
            </select>
          </div>

          {/* Collapsible aliases */}
          <button
            type="button"
            className="qa-modal-toggle"
            onClick={() => setShowAliases(!showAliases)}
          >
            {showAliases ? '- Hide' : '+ Add'} Name Aliases (optional)
          </button>
          {showAliases && (
            <div className="qa-modal-field">
              <label>Aliases <span style={{ fontWeight: 400, fontSize: '0.85em', color: '#888' }}>(alternate names for semantic search)</span></label>
              {aliases.length > 0 && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 6 }}>
                  {aliases.map((alias, idx) => (
                    <span
                      key={idx}
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        background: '#e8eaf6',
                        borderRadius: 16,
                        padding: '2px 10px',
                        fontSize: '0.92em',
                      }}
                    >
                      {alias}
                      <button
                        type="button"
                        onClick={() => setAliases(aliases.filter((_, i) => i !== idx))}
                        style={{
                          border: 'none',
                          background: 'transparent',
                          cursor: 'pointer',
                          marginLeft: 4,
                          fontSize: '1em',
                          lineHeight: 1,
                          color: '#666',
                        }}
                        title="Remove alias"
                      >
                        &times;
                      </button>
                    </span>
                  ))}
                </div>
              )}
              <div style={{ display: 'flex', gap: 6 }}>
                <input
                  type="text"
                  value={aliasInput}
                  onChange={(e) => setAliasInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      const text = aliasInput.trim();
                      if (text && !aliases.includes(text)) {
                        setAliases([...aliases, text]);
                      }
                      setAliasInput('');
                    }
                  }}
                  placeholder="Type an alias and press Enter"
                  style={{ flex: 1 }}
                />
                <button
                  type="button"
                  className="qa-btn qa-btn--secondary"
                  onClick={() => {
                    const text = aliasInput.trim();
                    if (text && !aliases.includes(text)) {
                      setAliases([...aliases, text]);
                    }
                    setAliasInput('');
                  }}
                  style={{ padding: '6px 14px' }}
                >
                  Add
                </button>
              </div>
            </div>
          )}

          {/* Collapsible source text */}
          <button
            type="button"
            className="qa-modal-toggle"
            onClick={() => setShowSourceText(!showSourceText)}
          >
            {showSourceText ? '- Hide' : '+ Add'} Source Text (optional)
          </button>
          {showSourceText && (
            <div className="qa-modal-field">
              <label>Source Text</label>
              <textarea
                value={sourceText}
                onChange={(e) => setSourceText(e.target.value)}
                placeholder="Verbatim quote from source document..."
                rows={3}
              />
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="qa-modal-footer">
          <button className="qa-btn qa-btn--secondary" onClick={onClose} disabled={saving}>
            Cancel
          </button>
          <button className="qa-btn qa-btn--primary" onClick={handleSubmit} disabled={saving}>
            {saving ? 'Creating...' : 'Create Node'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default QuickAddNodeModal;
